/**
 * Spec Phase 7 Slice 7E — body_html → React parser.
 *
 * Server-rendered HTML for auto-generated sections embeds numeral
 * references as ``<span class="s6-num" data-num="N">N</span>``.
 * Slice 7D rendered the whole body via dangerouslySetInnerHTML;
 * Slice 7E swaps in this parser so every chip becomes a
 * <NumeralChip> React component with hover/click handlers + the
 * undefined-numeral warn state.
 *
 * Approach:
 *   - Use DOMParser (browser built-in) to parse the HTML fragment
 *     once per render. This is fast and battle-tested for the
 *     server's narrow HTML vocabulary (table, tr, th, td, p,
 *     strong, span).
 *   - Walk the parsed tree recursively; for each element:
 *       * if .s6-num span → <NumeralChip data-num=…>
 *       * else → React.createElement(tag, props, …children)
 *     Text nodes pass through as strings.
 *   - Attribute pass-through: copy class, data-*, aria-*, colspan,
 *     scope, and a small allowlist (style intentionally NOT passed
 *     since the server doesn't emit inline styles).
 *
 * Defined-numeral lookup:
 *   - The caller passes a Set<string> of known numerals (read from
 *     the spec store's referenceSigns registry). Any chip whose
 *     data-num is NOT in the set is rendered with isUndefined=true
 *     so the chip styles itself amber.
 */
import React from 'react';
import NumeralChip from '../components/spec/NumeralChip';
import type { NumeralEvent } from './numeralEventBus';

interface ParseOptions {
  knownNumerals: Set<string>;
  source: NumeralEvent['source'];
  /** Optional label resolver; receives the raw numeral and returns
   * the registry label for tooltip text. Falls back to data-label
   * attribute if the server emitted it. */
  resolveLabel?: (numeral: string) => string | null;
}

/** ATTR_ALLOWLIST — ssr-safe attribute pass-through. We
 * deliberately whitelist; the server's render functions only emit
 * these. Any new server-side attribute lands in this list before
 * showing up on the client. */
const ATTR_ALLOWLIST = new Set([
  'class',
  'colspan',
  'rowspan',
  'scope',
  'href',
  'role',
  'aria-label',
  'aria-describedby',
  'data-claim',
  'data-numeral',
  'data-paragraph-number',
]);

let _keyCounter = 0;

function _attrsToProps(el: Element): Record<string, unknown> {
  const props: Record<string, unknown> = {};
  for (const a of Array.from(el.attributes)) {
    const name = a.name.toLowerCase();
    if (!ATTR_ALLOWLIST.has(name)) continue;
    if (name === 'class') {
      props.className = a.value;
    } else if (name === 'colspan') {
      props.colSpan = Number(a.value) || undefined;
    } else if (name === 'rowspan') {
      props.rowSpan = Number(a.value) || undefined;
    } else {
      props[name] = a.value;
    }
  }
  return props;
}

function _renderNode(
  node: Node,
  opts: ParseOptions,
): React.ReactNode {
  if (node.nodeType === Node.TEXT_NODE) {
    return node.textContent;
  }
  if (node.nodeType !== Node.ELEMENT_NODE) {
    return null;
  }
  const el = node as Element;
  const tag = el.tagName.toLowerCase();

  // Numeral chip → React component
  if (tag === 'span' && el.classList.contains('s6-num')) {
    const numeral = el.getAttribute('data-num') ?? el.textContent ?? '';
    const labelAttr = el.getAttribute('data-label');
    const isUndefined = !opts.knownNumerals.has(numeral);
    const resolvedLabel =
      labelAttr ?? (opts.resolveLabel?.(numeral) ?? null);
    return (
      <NumeralChip
        key={`num-${numeral}-${_keyCounter++}`}
        numeral={numeral}
        label={resolvedLabel}
        isUndefined={isUndefined}
        source={opts.source}
      />
    );
  }

  const props = _attrsToProps(el);
  const children: React.ReactNode[] = [];
  el.childNodes.forEach((child) => {
    const rendered = _renderNode(child, opts);
    if (rendered !== null && rendered !== undefined) children.push(rendered);
  });

  return React.createElement(
    tag,
    { ...props, key: `el-${tag}-${_keyCounter++}` },
    ...children,
  );
}

export function parseNumeralChips(
  html: string,
  opts: ParseOptions,
): React.ReactNode[] {
  // Reset per top-level parse so keys stay stable across components
  // mounted in the same render. Different parse() calls get
  // disjoint key prefixes from the caller wrapping each in a
  // <div key={paragraph_number}>.
  _keyCounter = 0;

  if (!html) return [];
  if (typeof DOMParser === 'undefined') {
    // Server-side render path — return raw text as a fallback. The
    // browser hydration replaces it on mount.
    return [html];
  }

  const doc = new DOMParser().parseFromString(
    `<!doctype html><html><body>${html}</body></html>`,
    'text/html',
  );
  const body = doc.body;
  const out: React.ReactNode[] = [];
  body.childNodes.forEach((n) => {
    const r = _renderNode(n, opts);
    if (r !== null && r !== undefined) out.push(r);
  });
  return out;
}
